import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAuth } from '@/core/hooks/useAuth';
import { useTheme } from '@/core/theme/ThemeProvider';
import { useSidebar } from '@/layout/Sidebar/SidebarContext';
import UserProfile from '@/shared/components/UserProfile/UserProfile';
import CommandPalette from '@/shared/components/CommandPalette/CommandPalette';
import type { SearchItem } from '@/shared/components/CommandPalette/CommandPalette';
import { Button } from '@/shared/components/ui/button';
import { Container, Moon, Sun, PanelLeftClose, PanelLeftOpen, Menu, Search } from 'lucide-react';

function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label="Toggle theme"
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </Button>
  );
}

interface HeaderProps {
  readonly commandPaletteItems?: SearchItem[];
}

export default function Header({ commandPaletteItems }: HeaderProps) {
  const isAuthenticated = useIsAuthenticated();
  const { login } = useAuth();
  const { collapsed, toggle, toggleMobile } = useSidebar();
  const [commandOpen, setCommandOpen] = useState(false);

  const extraItems = useMemo(() => commandPaletteItems ?? [], [commandPaletteItems]);

  // Global Ctrl+K / Cmd+K handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 items-center px-6">
          <div className="flex items-center gap-1">
            {/* Mobile hamburger */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobile}
              aria-label="Open navigation"
              className="md:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>
            {/* Desktop sidebar toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggle}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="hidden md:inline-flex"
            >
              {collapsed ? (
                <PanelLeftOpen className="h-5 w-5" />
              ) : (
                <PanelLeftClose className="h-5 w-5" />
              )}
            </Button>
            <Link to="/" className="flex items-center gap-2 font-semibold">
              <Container className="h-6 w-6 text-primary" />
              <span className="text-lg hidden sm:inline">AppOps Console</span>
            </Link>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* Search trigger */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCommandOpen(true)}
              className="hidden sm:flex items-center gap-2 text-muted-foreground"
            >
              <Search className="h-4 w-4" />
              <span className="text-sm">Search...</span>
              <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                Ctrl+K
              </kbd>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCommandOpen(true)}
              className="sm:hidden"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </Button>
            <ThemeToggle />
            {isAuthenticated ? (
              <UserProfile />
            ) : (
              <Button variant="default" size="sm" onClick={login}>
                Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <CommandPalette
        open={commandOpen}
        onOpenChange={setCommandOpen}
        extraItems={extraItems}
      />
    </>
  );
}
